import React from 'react'
import { WarningOutlined } from '@ant-design/icons'
import { formatDateTimeStr } from '@/utils/renderUtils'

/** 预警已持续时长（大屏展示用） */
export function formatSurveillanceAlertDuration (durationMs) {
  if (!durationMs || durationMs < 0) return '0秒'
  const totalSec = Math.floor(durationMs / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}时${m}分${s}秒`
  if (m > 0) return `${m}分${s}秒`
  return `${s}秒`
}

/** Pad 密看：去掉秒，长时只保留时分，缩短占位 */
function formatSurveillanceAlertDurationCompact (durationMs) {
  if (!durationMs || durationMs < 0) return '0秒'
  const totalSec = Math.floor(durationMs / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}时${m}分`
  if (m > 0) return `${m}分${s}秒`
  return `${s}秒`
}

/** Pad 密看：MM-DD HH:mm，避免与「已持续」抢同一行 */
function formatSurveillanceAlertIssuedCompact (eventTimeMs) {
  if (!eventTimeMs) return '—'
  const d = new Date(eventTimeMs)
  if (Number.isNaN(d.getTime())) return '—'
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * 监控墙预警条：等级并入徽章，旁侧为异常说明（面向医护远距扫视）
 * @param {string} [badgeTitle] 无婴儿用「无婴儿」，其它用「异常预警」
 * @param {boolean} [compact] Pad 密看：更短文案 + 纵向 metrics，避免窄格互盖
 * @param {boolean} [flash] 非无婴儿高预警：顶栏呼吸闪烁
 */
export default function SurveillanceAlertOverlay ({
  badgeTitle = '异常预警',
  levelText = '预警',
  levelColor = 'red',
  statusText = '',
  eventTimeMs,
  durationMs = 0,
  severe = false,
  flash = false,
  compact = false
}) {
  const issuedAtText = compact
    ? formatSurveillanceAlertIssuedCompact(eventTimeMs)
    : (eventTimeMs ? formatDateTimeStr(eventTimeMs) : '—')
  const durationText = compact
    ? formatSurveillanceAlertDurationCompact(durationMs)
    : formatSurveillanceAlertDuration(durationMs)
  const levelLabel = String(levelText || '').trim() || '预警'
  const titleLabel = String(badgeTitle || '').trim() || '异常预警'

  return (
    <div
      className={[
        'surveillance-alert-overlay',
        `surveillance-alert-overlay--${levelColor}`,
        severe ? 'surveillance-alert-overlay--severe' : '',
        flash ? 'surveillance-alert-overlay--flash' : '',
        compact ? 'surveillance-alert-overlay--compact' : ''
      ]
        .filter(Boolean)
        .join(' ')}
      role="status"
      aria-live="polite"
    >
      <div className="surveillance-alert-overlay__status-row">
        <span className="surveillance-alert-overlay__badge">
          <WarningOutlined className="surveillance-alert-overlay__badge-ico" />
          <span className="surveillance-alert-overlay__badge-title">{titleLabel}</span>
          <span className="surveillance-alert-overlay__badge-level">{levelLabel}</span>
        </span>
        {/* 始终占位，避免有无 statusText 导致卡片高低不一 */}
        <span className="surveillance-alert-overlay__type" title={statusText || undefined}>
          {statusText || '\u00A0'}
        </span>
      </div>
      <div className="surveillance-alert-overlay__metrics-row">
        <div className="surveillance-alert-overlay__metric surveillance-alert-overlay__metric--duration">
          <span className="surveillance-alert-overlay__metric-label">已持续</span>
          <span className="surveillance-alert-overlay__metric-value surveillance-alert-overlay__metric-value--duration">
            {durationText}
          </span>
        </div>
        <div className="surveillance-alert-overlay__metric surveillance-alert-overlay__metric--issued">
          <span className="surveillance-alert-overlay__metric-label">发出</span>
          <span className="surveillance-alert-overlay__metric-value surveillance-alert-overlay__metric-value--issued">
            {issuedAtText}
          </span>
        </div>
      </div>
    </div>
  )
}
